import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ExcelJS from 'exceljs';
import cashReservesService from '../../services/cashReservesService';
import SortableHeader from './SortableHeader';
import { fmtCurrency, sortByColumn } from './helpers';
import { downloadBlob } from '../../utils/downloadBlob';

/**
 * RmdClientsTab — accounts whose owner must take a required minimum
 * distribution this year, plus every inherited IRA.
 *
 * CS checks it several times a year (most of all before mid-December) to make
 * sure every client of RMD age has been contacted, including clients onboarded
 * during the year. The list is rebuilt from the daily Tamarac reports on every
 * load; nothing is tracked here.
 *
 * Birth dates come from Tamarac's Client Information report, matched to each
 * account by household and owner name. Accounts whose owner can't be matched
 * are listed under Missing DOB rather than dropped.
 */

export const SECTIONS = [
  {
    key: 'rmd_age',
    label: 'RMD Age',
    icon: 'bi-person-check',
    description:
      'Traditional, SEP and SIMPLE IRAs, Individual 401(k)s, and supported workplace retirement plans whose owner reaches RMD age by year-end.',
  },
  {
    key: 'inherited',
    label: 'Inherited IRAs',
    icon: 'bi-diagram-3',
    description: 'Every open inherited IRA, by account type alone (the owner’s age does not matter).',
  },
  {
    key: 'missing_dob',
    label: 'Missing DOB',
    icon: 'bi-question-circle',
    description:
      'Retirement accounts whose owner has no birth date in Tamarac Client Information or the CRM contact. Check these by hand: any could be of RMD age.',
  },
];

export const REASON_LABELS = {
  no_client_record: 'No client record for owner',
  client_has_no_dob: 'Client has no DOB in Tamarac',
  ambiguous: 'More than one possible client',
  owner_name_unparsed: 'Owner not in account name',
  dob_report_unavailable: 'Client Information report unavailable',
  owner_rejected: 'Owner match rejected',
};

// How the owner's client record was found. Exact names and the nickname on the
// client's CRM contact are facts; the rest are guesses worth flagging: a
// shortened "Chris" may have matched Christine while the owner is Christopher.
export const OWNER_MATCH_LABELS = {
  exact: 'Exact name',
  crm_nickname: 'CRM nickname',
  nickname: 'Shortened name',
  middle_name: 'Middle name',
  // Inferred when nothing else matched: guesses for CS to confirm.
  common_nickname: 'Common nickname',
  email_name: 'Email name',
  common_nickname_email: 'Common nickname + email',
  partial_surname: 'Partial surname',
};

const CONFIRMED_MATCHES = new Set(['exact', 'crm_nickname']);
// Which client record the owner's birth date came from.
const DOB_SOURCE_LABELS = {
  report: 'Client Information',
  crm: 'CRM contact',
};

// A guessed match nobody has confirmed yet. CS decisions are stored per
// account (owner_review) and hold while the account matches the same client.
const needsOwnerCheck = (row) =>
  Boolean(row.owner_match) && !CONFIRMED_MATCHES.has(row.owner_match) && !row.owner_review;

const reviewedBy = (review) => {
  const when = review?.at ? new Date(review.at).toLocaleDateString() : '';
  return [review?.by, when].filter(Boolean).join(' on ');
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
};

const flagLabel = (row) => (row.not_flagged_in_tamarac ? 'No' : 'Yes');
// Mirrors rmd_report._is_flagged.
const rmdAnnualOn = (row) => ['yes', 'y', 'true', '1'].includes(String(row.rmd_annual ?? '').trim().toLowerCase());

// Met / not met needs both an RMD Amount and a known Taken YTD (the backend
// leaves rmd_remaining null otherwise), so those accounts match neither.
const rmdStatus = (row) => {
  if (row.rmd_remaining == null) return null;
  return row.rmd_remaining === 0 ? 'met' : 'not_met';
};

const DobConflictBadge = ({ row }) =>
  row.dob_conflict ? (
    <span
      className="badge bg-warning-subtle text-warning-emphasis ms-2"
      title={`Tamarac and the CRM record different birth dates for this client: ${row.dob_conflict
        .map(fmtDate)
        .join(' and ')}. Using the CRM contact's; correct whichever record is wrong.`}
    >
      DOB conflict
    </span>
  ) : null;

// Taken YTD against the RMD Amount, as one cell: "$1,000 of $1,235", a thin
// bar, and what's left. RMD Amount 0 means none was entered in Tamarac.
const RmdProgress = ({ row }) => {
  const amount = row.rmd_amount || 0;
  const taken = row.rmd_taken_ytd;
  if (!amount) {
    if (taken == null) return <span className="text-muted">—</span>;
    return (
      <>
        <div>{fmtCurrency(taken)} taken</div>
        <div className="text-muted small">No RMD Amount in Tamarac</div>
      </>
    );
  }
  if (taken == null) {
    return (
      <>
        <div>{fmtCurrency(amount)} RMD</div>
        <div className="text-muted small">Taken unavailable</div>
      </>
    );
  }
  const met = row.rmd_remaining === 0;
  const percent = Math.min(100, Math.max(0, (taken / amount) * 100));
  return (
    <>
      <div>
        {fmtCurrency(taken)} <span className="text-muted">of</span> {fmtCurrency(amount)}
      </div>
      <div
        className="progress my-1"
        style={{ height: '4px' }}
        role="progressbar"
        aria-label="Share of RMD taken"
        aria-valuenow={Math.round(percent)}
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <div className={`progress-bar ${met ? 'bg-success' : 'bg-primary'}`} style={{ width: `${percent}%` }}></div>
      </div>
      <div className={`small ${met ? 'text-success' : 'text-muted'}`}>
        {met ? 'Met' : `${fmtCurrency(row.rmd_remaining)} left`}
      </div>
    </>
  );
};

// Column definitions per section: header, sort accessor, cell renderer and the
// Excel value, so the table and the workbook can't drift apart.
const COLUMNS = {
  household: {
    label: 'Household',
    sort: (r) => r.household_name || '',
    cell: (r) => r.household_name || <span className="text-muted">—</span>,
    excel: (r) => r.household_name || '',
    width: 30,
  },
  account: {
    label: 'Account',
    sort: (r) => r.account_name || '',
    cell: (r) => (
      <>
        <div>
          {r.account_name}
          {r.account_name_typo && (
            <span
              className="badge bg-info-subtle text-info-emphasis ms-2"
              title='The account name has a period instead of a comma after the last name ("Last. First"). Matched to the client record anyway; fix the name in Tamarac.'
            >
              Name typo
            </span>
          )}
        </div>
        <div className="text-muted small">
          {r.account_number}
          {r.account_type && <span title={r.category_label}> · {r.account_type}</span>}
        </div>
      </>
    ),
    excel: (r) => r.account_name || '',
    width: 36,
  },
  accountNumber: {
    label: 'Account #',
    excel: (r) => r.account_number,
    width: 14,
  },
  accountType: {
    label: 'Account Type',
    excel: (r) => r.account_type || '',
    width: 28,
  },
  owner: {
    label: 'Owner',
    sort: (r) => r.owner_name || '',
    cell: (r, actions = {}) => {
      const saving = actions.savingAccount === r.account_number;
      const linkButton = 'btn btn-link btn-sm p-0 ms-2 text-decoration-none';
      return (
        <>
          {r.owner_name}
          {needsOwnerCheck(r) && (
            <>
              <span
                className="badge bg-warning-subtle text-warning-emphasis ms-2"
                title={`Matched to the client record by ${OWNER_MATCH_LABELS[r.owner_match]?.toLowerCase() || r.owner_match}, not an exact first name. Confirm this is the account owner and their DOB.`}
              >
                Check owner
              </span>
              <button
                type="button"
                className={linkButton}
                disabled={saving}
                onClick={() => actions.onReview?.(r, 'confirmed')}
              >
                Confirm
              </button>
              <button
                type="button"
                className={`${linkButton} text-danger`}
                disabled={saving}
                onClick={() => actions.onReview?.(r, 'rejected')}
              >
                Not this person
              </button>
            </>
          )}
          {r.owner_review && (
            <>
              <span
                className={`badge ms-2 ${
                  r.owner_review.decision === 'confirmed'
                    ? 'bg-secondary-subtle text-secondary-emphasis'
                    : 'bg-danger-subtle text-danger-emphasis'
                }`}
                title={`${r.owner_review.decision === 'confirmed' ? 'Confirmed' : 'Rejected'} by ${reviewedBy(r.owner_review)}`}
              >
                {r.owner_review.decision === 'confirmed' ? 'Confirmed' : 'Rejected'}
              </span>
              <button
                type="button"
                className={linkButton}
                disabled={saving}
                onClick={() => actions.onUndo?.(r)}
              >
                Undo
              </button>
            </>
          )}
          {r.birth_date && (
            <div className="text-muted small">
              DOB {fmtDate(r.birth_date)}
              <DobConflictBadge row={r} />
            </div>
          )}
        </>
      );
    },
    excel: (r) => r.owner_name || '',
    width: 24,
  },
  ownerMatch: {
    label: 'Owner Match',
    excel: (r) => {
      if (r.owner_review?.decision === 'rejected') return 'Rejected';
      const label = OWNER_MATCH_LABELS[r.owner_match] || '';
      return label && r.owner_review?.decision === 'confirmed' ? `${label} (confirmed)` : label;
    },
    width: 22,
  },
  birthDate: {
    label: 'DOB',
    excel: (r) => fmtDate(r.birth_date),
    width: 12,
  },
  dobConflict: {
    label: 'DOB Conflict',
    excel: (r) => (r.dob_conflict ? r.dob_conflict.map(fmtDate).join(' / ') : ''),
    width: 24,
  },
  age: {
    label: 'Age at Year-End',
    sort: (r) => (r.age_at_year_end == null ? -1 : r.age_at_year_end),
    cell: (r) => (
      <>
        {r.age_at_year_end}
        {r.first_rmd_year && (
          <span
            className="badge bg-info-subtle text-info-emphasis ms-2"
            title="First RMD year: the first distribution can wait until April 1 of next year"
          >
            First RMD
          </span>
        )}
      </>
    ),
    excel: (r) => r.age_at_year_end ?? '',
    width: 10,
  },
  dobSource: {
    label: 'DOB Source',
    excel: (r) => DOB_SOURCE_LABELS[r.owner_source] || '',
    width: 18,
  },
  firstRmd: {
    label: 'First RMD Year',
    excel: (r) => (r.first_rmd_year ? 'Yes' : ''),
    width: 10,
  },
  tenYear: {
    label: '10-Year Deadline',
    sort: (r) => r.rmd_10_year_rule || '',
    cell: (r) => fmtDate(r.rmd_10_year_rule) || <span className="text-muted">—</span>,
    excel: (r) => fmtDate(r.rmd_10_year_rule),
    width: 14,
  },
  flagged: {
    label: 'Flagged in Tamarac',
    excel: flagLabel,
    width: 10,
  },
  amount: {
    label: 'RMD Amount',
    excel: (r) => (r.rmd_amount != null ? r.rmd_amount : ''),
    width: 14,
  },
  withdrawalType: {
    label: 'Withdrawal Type',
    excel: (r) => r.rmd_withdrawal_type || '',
    width: 14,
  },
  taken: {
    label: 'RMD Taken YTD',
    excel: (r) => (r.rmd_taken_ytd != null ? r.rmd_taken_ytd : ''),
    width: 14,
  },
  remaining: {
    label: 'RMD Remaining',
    excel: (r) => (r.rmd_remaining != null ? r.rmd_remaining : ''),
    width: 14,
  },
  // Table-only: Flagged and Withdrawal Type as one badge column (the export
  // keeps them apart).
  tamarac: {
    label: 'Tamarac',
    sort: (r) => (r.not_flagged_in_tamarac ? 0 : 1),
    cell: (r) =>
      r.not_flagged_in_tamarac ? (
        <span
          className="badge bg-warning-subtle text-warning-emphasis"
          title="Neither RMD Annual = Yes nor an RMD Recurring Withdrawal Type is set in Tamarac"
        >
          Not flagged
        </span>
      ) : (
        <div className="d-flex flex-wrap gap-1">
          {r.rmd_withdrawal_type && (
            <span className="badge bg-success-subtle text-success-emphasis" title="RMD Recurring Withdrawal Type">
              {r.rmd_withdrawal_type}
            </span>
          )}
          {rmdAnnualOn(r) && (
            <span className="badge bg-success-subtle text-success-emphasis" title="RMD Annual = Yes">
              RMD Annual
            </span>
          )}
        </div>
      ),
  },
  // Table-only: RMD Amount, Taken YTD and Remaining in one cell, sorted by
  // what's still owed (no RMD Amount sorts as the least).
  rmd: {
    label: 'RMD Taken YTD',
    className: 'text-end',
    sort: (r) => r.rmd_remaining ?? -1,
    cell: (r) => (
      <div className="ms-auto" style={{ minWidth: '150px', maxWidth: '190px' }}>
        <RmdProgress row={r} />
      </div>
    ),
  },
  reason: {
    label: 'Why No DOB',
    sort: (r) => REASON_LABELS[r.match_reason] || r.match_reason || '',
    cell: (r) => REASON_LABELS[r.match_reason] || r.match_reason,
    excel: (r) => REASON_LABELS[r.match_reason] || r.match_reason || '',
    width: 32,
  },
};

const RMD_COLUMNS = ['flagged', 'withdrawalType', 'amount', 'taken', 'remaining'];
const CURRENCY_COLUMNS = ['amount', 'taken', 'remaining'];

// The table folds account type into Account, DOB into Owner, and the RMD
// fields into Tamarac and RMD Taken YTD; the workbook keeps each field apart.
const TABLE_COLUMNS = {
  rmd_age: ['household', 'account', 'owner', 'age', 'tamarac', 'rmd'],
  inherited: ['household', 'account', 'owner', 'tenYear', 'tamarac', 'rmd'],
  missing_dob: ['household', 'account', 'owner', 'reason', 'tamarac', 'rmd'],
};

const EXCEL_COLUMNS = {
  rmd_age: ['household', 'account', 'accountNumber', 'accountType', 'owner', 'ownerMatch', 'birthDate', 'dobSource', 'dobConflict', 'age', 'firstRmd', ...RMD_COLUMNS],
  inherited: ['household', 'account', 'accountNumber', 'accountType', 'owner', 'ownerMatch', 'birthDate', 'dobSource', 'dobConflict', 'tenYear', ...RMD_COLUMNS],
  missing_dob: ['household', 'account', 'accountNumber', 'accountType', 'owner', 'reason', ...RMD_COLUMNS],
};

const SORT_ACCESSORS = Object.fromEntries(
  Object.entries(COLUMNS)
    .filter(([, column]) => column.sort)
    .map(([key, column]) => [key, column.sort])
);

const matchesSearch = (row, term) => {
  if (!term) return true;
  return [row.household_name, row.account_name, row.account_number, row.owner_name, row.account_type]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(term));
};

export const buildRmdWorkbook = (report) => {
  const workbook = new ExcelJS.Workbook();
  SECTIONS.forEach((section) => {
    const worksheet = workbook.addWorksheet(section.label);
    worksheet.columns = EXCEL_COLUMNS[section.key].map((key) => ({
      header: COLUMNS[key].label,
      key,
      width: COLUMNS[key].width,
    }));
    worksheet.getRow(1).font = { bold: true };
    worksheet.views = [{ state: 'frozen', ySplit: 1 }];
    (report[section.key] || []).forEach((row) => {
      worksheet.addRow(
        Object.fromEntries(EXCEL_COLUMNS[section.key].map((key) => [key, COLUMNS[key].excel(row)]))
      );
    });
    CURRENCY_COLUMNS.forEach((key) => {
      worksheet.getColumn(key).numFmt = '$#,##0.00';
    });
  });
  return workbook;
};

const exportRmdToExcel = async (report) => {
  const buffer = await buildRmdWorkbook(report).xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  downloadBlob(blob, `rmd_clients_${report.year}_${new Date().toISOString().slice(0, 10)}.xlsx`);
};

const RmdClientsTab = () => {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [section, setSection] = useState('rmd_age');
  const [searchTerm, setSearchTerm] = useState('');
  const [onlyNotFlagged, setOnlyNotFlagged] = useState(false);
  const [onlyFirstYear, setOnlyFirstYear] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  // Without Taken YTD nothing is met or not met: the filter is off and disabled.
  const activeStatus = report?.rmd_taken_available ? statusFilter : 'all';
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showUnclassified, setShowUnclassified] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const load = useCallback(async (targetYear) => {
    setIsLoading(true);
    setError('');
    try {
      setReport(await cashReservesService.getRmdClients(targetYear));
    } catch (err) {
      setReport(null);
      setError(err?.response?.data?.detail || err?.message || 'Failed to load RMD clients.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(year);
  }, [load, year]);

  // Owner decisions ("Check owner"): save, then reload the report without the
  // loading spinner, so a rejected row moves to Missing DOB in place.
  const [savingAccount, setSavingAccount] = useState(null);
  const runOwnerAction = async (row, action) => {
    setSavingAccount(row.account_number);
    setError('');
    try {
      await action();
      setReport(await cashReservesService.getRmdClients(year));
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Could not save the owner decision.');
    } finally {
      setSavingAccount(null);
    }
  };
  const ownerActions = {
    savingAccount,
    onReview: (row, decision) =>
      runOwnerAction(row, () =>
        cashReservesService.reviewRmdOwner(row.account_number, {
          decision,
          client_key: row.client_key,
          owner_name: row.owner_name,
          account_name: row.account_name,
        })
      ),
    onUndo: (row) =>
      runOwnerAction(row, () => cashReservesService.undoRmdOwnerReview(row.account_number)),
  };

  const handleSort = (key) =>
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));

  const rows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = (report?.[section] || []).filter(
      (row) =>
        matchesSearch(row, term) &&
        (!onlyNotFlagged || row.not_flagged_in_tamarac) &&
        (!onlyFirstYear || section !== 'rmd_age' || row.first_rmd_year) &&
        (activeStatus === 'all' || rmdStatus(row) === activeStatus)
    );
    return sortByColumn(filtered, SORT_ACCESSORS, sortConfig) || filtered;
  }, [report, section, searchTerm, onlyNotFlagged, onlyFirstYear, activeStatus, sortConfig]);

  const handleExport = async () => {
    if (!report) return;
    setIsExporting(true);
    try {
      await exportRmdToExcel(report);
    } catch (err) {
      setError(`Export failed: ${err?.message || err}`);
    } finally {
      setIsExporting(false);
    }
  };

  const activeSection = SECTIONS.find((s) => s.key === section);
  const total = report?.[section]?.length ?? 0;
  const unclassified = report?.unclassified_types || [];

  return (
    <div>
      <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
        <label className="d-flex align-items-center gap-2 mb-0">
          <span className="text-muted small">Year</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            aria-label="RMD year"
          >
            {[thisYear - 1, thisYear, thisYear + 1].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        <div className="btn-group" role="group" aria-label="RMD sections">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`btn btn-sm ${section === s.key ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setSection(s.key)}
              aria-pressed={section === s.key}
            >
              <i className={`bi ${s.icon} me-1`}></i>
              {s.label}
              <span className={`badge ms-2 ${section === s.key ? 'bg-light text-primary' : 'bg-primary'}`}>
                {isLoading || !report ? '—' : report.counts?.[s.key] ?? 0}
              </span>
            </button>
          ))}
        </div>
        <div className="ms-auto d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={handleExport}
            disabled={!report || isLoading || isExporting}
            title="Download all three lists as an Excel workbook, one sheet each"
          >
            <i className="bi bi-file-earmark-excel me-1"></i>
            {isExporting ? 'Exporting…' : 'Export Excel'}
          </button>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => load(year)}
            disabled={isLoading}
          >
            <i className={`bi bi-arrow-clockwise me-1 ${isLoading ? 'spin' : ''}`}></i>
            Reload
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          <div>{error}</div>
        </div>
      )}

      {report && !report.dob_report_available && (
        <div className="alert alert-warning" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          The Tamarac Client Information report is unavailable, so no birth dates could be read. Every
          retirement account is listed under <strong>Missing DOB</strong> until it is back.
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-header d-flex align-items-center flex-wrap gap-3">
          <div>
            <div className="fw-semibold">
              <i className={`bi ${activeSection.icon} me-2 text-primary`}></i>
              {activeSection.label}
              {report && <span className="text-muted fw-normal"> · {report.year}</span>}
            </div>
            <div className="text-muted small">{activeSection.description}</div>
            {report?.rmd_taken_available && report.rmd_taken_start && (
              <div className="text-muted small">
                RMD Taken YTD: withdrawals and tax withheld, excluding fees, traded{' '}
                {fmtDate(report.rmd_taken_start)}–{fmtDate(report.rmd_taken_through)}.
              </div>
            )}
            {report && !report.rmd_taken_available && report.rmd_taken_start && (
              <div className="text-muted small">
                <i className="bi bi-info-circle me-1"></i>
                RMD Taken YTD unavailable: transaction history could not be read.
              </div>
            )}
          </div>
          <div className="ms-auto d-flex align-items-center flex-wrap gap-3">
            <input
              type="search"
              className="form-control form-control-sm"
              style={{ width: '240px' }}
              placeholder="Search name, account, household"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search RMD clients"
            />
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={activeStatus}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="RMD status"
              title="Met and Not met only include accounts with an RMD Amount in Tamarac"
              disabled={!report?.rmd_taken_available}
            >
              <option value="all">All RMD statuses</option>
              <option value="met">RMD met</option>
              <option value="not_met">RMD not met</option>
            </select>
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="rmd-only-not-flagged"
                checked={onlyNotFlagged}
                onChange={(e) => setOnlyNotFlagged(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="rmd-only-not-flagged">
                Not flagged for RMD in Tamarac
              </label>
            </div>
            {section === 'rmd_age' && (
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="rmd-only-first-year"
                  checked={onlyFirstYear}
                  onChange={(e) => setOnlyFirstYear(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="rmd-only-first-year">
                  First RMD year
                </label>
              </div>
            )}
          </div>
        </div>

        <div className="card-body p-0">
          {isLoading ? (
            <div className="text-center text-muted py-5">
              <div className="spinner-border spinner-border-sm me-2" role="status"></div>
              Loading RMD clients…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center text-muted py-5">
              {report ? 'No accounts match.' : 'No data.'}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    {TABLE_COLUMNS[section].map((key) => (
                      <SortableHeader
                        key={key}
                        label={COLUMNS[key].label}
                        sortKey={key}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className={COLUMNS[key].className}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.account_number}>
                      {TABLE_COLUMNS[section].map((key) => (
                        <td key={key} className={COLUMNS[key].className}>
                          {COLUMNS[key].cell(row, ownerActions)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {report && !isLoading && (
          <div className="card-footer text-muted small">
            Showing {rows.length} of {total} account{total !== 1 ? 's' : ''}. Built from the daily Tamarac
            reports{report.generated_at ? ` at ${new Date(report.generated_at).toLocaleString()}` : ''}.
            {report.dob_as_of && ` Birth dates as of ${fmtDate(report.dob_as_of)}.`}
            {report.excluded_employee_accounts > 0 &&
              ` ${report.excluded_employee_accounts} employee account${
                report.excluded_employee_accounts !== 1 ? 's' : ''
              } not listed.`}
          </div>
        )}
      </div>

      {unclassified.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-decoration-none"
            onClick={() => setShowUnclassified((v) => !v)}
            aria-expanded={showUnclassified}
          >
            <i className={`bi ${showUnclassified ? 'bi-chevron-down' : 'bi-chevron-right'} me-1`}></i>
            Account types not included ({unclassified.length})
          </button>
          {showUnclassified && (
            <div className="card card-body mt-2 small">
              <p className="text-muted mb-2">
                Open accounts whose type isn’t a Traditional, SEP or SIMPLE IRA, an Individual 401(k) or an
                inherited IRA. If an RMD account appears here, its type is spelled in a way the list
                doesn’t recognise yet.
              </p>
              <ul className="list-unstyled mb-0" style={{ columns: '3 220px' }}>
                {unclassified.map((u) => (
                  <li key={u.account_type}>
                    {u.account_type} <span className="text-muted">({u.count})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RmdClientsTab;
